import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { usePlan } from '../contexts/PlanContext';
import {
  CreditCard,
  Crown,
  Headset,
  ArrowRight,
  Clock,
  CheckCircle,
  Warning,
  Receipt,
  CalendarBlank,
  ShieldCheck,
  XCircle,
  ArrowsClockwise,
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  getBillingOverview,
  getBillingHistory,
  cancelSubscription,
  reactivateSubscription,
  subscribeCustomerService,
  cancelCustomerService,
} from '../lib/api';

const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    past_due: 'bg-amber-100 text-amber-700 border-amber-200',
    canceled: 'bg-red-100 text-red-700 border-red-200',
    trialing: 'bg-blue-100 text-blue-700 border-blue-200',
    pending: 'bg-gray-100 text-gray-600 border-gray-200',
    paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    unpaid: 'bg-amber-100 text-amber-700 border-amber-200',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full border ${styles[status] || styles.pending}`} data-testid={`status-badge-${status}`}>
      {status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
    </span>
  );
};

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function BillingPage() {
  const { tx } = useLanguage();
  const { plan_name, handleUpgrade, refreshPlan, customer_service_active } = usePlan();
  const [billing, setBilling] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [csLoading, setCsLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [billingRes, historyRes] = await Promise.all([
        getBillingOverview(),
        getBillingHistory(),
      ]);
      setBilling(billingRes.data);
      setHistory(historyRes.data.transactions || []);
    } catch (err) {
      console.error('Failed to load billing data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      const res = await cancelSubscription();
      toast.success(res.data.message);
      setShowCancelConfirm(false);
      await fetchData();
      refreshPlan();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivate = async () => {
    setReactivateLoading(true);
    try {
      const res = await reactivateSubscription();
      toast.success(res.data.message);
      await fetchData();
      refreshPlan();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate');
    } finally {
      setReactivateLoading(false);
    }
  };

  const handleSubscribeCS = async () => {
    setCsLoading(true);
    try {
      const res = await subscribeCustomerService();
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setCsLoading(false);
    }
  };

  const handleCancelCS = async () => {
    setCsLoading(true);
    try {
      const res = await cancelCustomerService();
      toast.success(res.data.message);
      await fetchData();
      refreshPlan();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel add-on');
    } finally {
      setCsLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Billing">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#0077FF] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const sub = billing?.subscription;
  const csSub = billing?.customer_service_subscription;
  const isCancelScheduled = sub?.cancel_at_period_end;
  const pm = sub?.default_payment_method;

  return (
    <Layout title="Billing">
      <div className="space-y-6 max-w-5xl" data-testid="billing-page">

        {/* Current Plan Card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-[#003166] rounded-xl">
                  <Crown size={24} weight="fill" className="text-[#FF9900]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.04em' }} data-testid="billing-plan-name">
                    {billing?.plan_name} Plan
                  </h2>
                  <div className="flex items-center gap-2 mt-0.5">
                    <StatusBadge status={billing?.subscription_status} />
                    {isCancelScheduled && (
                      <span className="text-xs text-amber-600 font-medium">Cancels {formatDate(sub?.current_period_end)}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Bebas Neue' }} data-testid="billing-price">
                  ${billing?.price_usd}<span className="text-sm font-normal text-gray-500">/mo</span>
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {/* Next billing date */}
            <div className="flex items-start gap-3">
              <CalendarBlank size={20} className="text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Next Billing Date</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5" data-testid="billing-next-date">
                  {sub ? formatDate(sub.current_period_end) : '—'}
                </p>
              </div>
            </div>
            {/* Payment method */}
            <div className="flex items-start gap-3">
              <CreditCard size={20} className="text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Payment Method</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5" data-testid="billing-payment-method">
                  {pm ? `${pm.brand?.toUpperCase()} ****${pm.last4} (${pm.exp_month}/${pm.exp_year})` : 'No card on file'}
                </p>
              </div>
            </div>
            {/* Member since */}
            <div className="flex items-start gap-3">
              <ShieldCheck size={20} className="text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Member Since</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5" data-testid="billing-member-since">
                  {billing?.subscription_activated_at ? formatDate(billing.subscription_activated_at) : '—'}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 flex flex-wrap gap-3">
            {billing?.plan !== 'enterprise' && billing?.plan !== 'pro' && (
              <button onClick={handleUpgrade} className="px-5 py-2 bg-[#FF9900] hover:bg-[#e08800] text-white text-sm font-bold rounded-lg transition-colors flex items-center gap-2" data-testid="billing-upgrade-btn">
                <ArrowRight size={16} /> Upgrade to Professional
              </button>
            )}
            {sub && !isCancelScheduled && (
              <button onClick={() => setShowCancelConfirm(true)} className="px-5 py-2 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium rounded-lg transition-colors" data-testid="billing-cancel-btn">
                Cancel Subscription
              </button>
            )}
            {sub && isCancelScheduled && (
              <button onClick={handleReactivate} disabled={reactivateLoading} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-lg transition-colors flex items-center gap-2 disabled:opacity-60" data-testid="billing-reactivate-btn">
                <ArrowsClockwise size={16} /> {reactivateLoading ? 'Reactivating...' : 'Reactivate Subscription'}
              </button>
            )}
          </div>
        </motion.div>

        {/* Cancel Confirmation Modal */}
        {showCancelConfirm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cancel-confirm-modal">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Warning size={24} weight="fill" className="text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Cancel Subscription?</h3>
              </div>
              <p className="text-sm text-gray-600 mb-2">Your subscription will remain active until the end of the current billing period:</p>
              <p className="text-sm font-semibold text-gray-900 mb-4">{sub ? formatDate(sub.current_period_end) : '—'}</p>
              <p className="text-xs text-gray-500 mb-6">After this date, you will lose access to your plan features. You can reactivate at any time before the period ends.</p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowCancelConfirm(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" data-testid="cancel-confirm-back-btn">
                  Keep Subscription
                </button>
                <button onClick={handleCancel} disabled={cancelLoading} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-lg transition-colors disabled:opacity-60" data-testid="cancel-confirm-yes-btn">
                  {cancelLoading ? 'Cancelling...' : 'Yes, Cancel'}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* 24/7 Customer Service Add-on */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${billing?.customer_service_active ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                  <Headset size={24} weight="duotone" className={billing?.customer_service_active ? 'text-emerald-600' : 'text-gray-500'} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.04em' }} data-testid="cs-addon-title">
                    24/7 Courier Customer Service
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">Professional agents handle all customer inquiries on your behalf</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Bebas Neue' }}>$99<span className="text-xs font-normal text-gray-500">/mo</span></span>
                {billing?.customer_service_active ? (
                  <div className="flex items-center gap-2">
                    <StatusBadge status="active" />
                    {csSub && !csSub.cancel_at_period_end && (
                      <button onClick={handleCancelCS} disabled={csLoading} className="text-xs text-red-500 hover:text-red-700 font-medium disabled:opacity-60" data-testid="cs-cancel-btn">
                        {csLoading ? '...' : 'Cancel'}
                      </button>
                    )}
                    {csSub?.cancel_at_period_end && (
                      <span className="text-xs text-amber-600">Cancels {formatDate(csSub.current_period_end)}</span>
                    )}
                  </div>
                ) : (
                  <button onClick={handleSubscribeCS} disabled={csLoading} className="px-4 py-2 bg-[#FF9900] hover:bg-[#e08800] text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-60" data-testid="cs-subscribe-btn">
                    {csLoading ? 'Loading...' : 'Subscribe'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Billing History */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <Receipt size={22} weight="duotone" className="text-gray-400" />
              <h3 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.04em' }}>
                Billing History
              </h3>
            </div>
          </div>
          <div className="overflow-x-auto">
            {history.length > 0 ? (
              <table className="w-full" data-testid="billing-history-table">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Plan</th>
                    <th className="text-right py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="text-center py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-center py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Recurring</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((tx, i) => (
                    <tr key={tx.id || i} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                      <td className="py-3.5 px-6 text-sm text-gray-700">{formatDate(tx.created_at)}</td>
                      <td className="py-3.5 px-6 text-sm text-gray-700 capitalize">{tx.type?.replace('_', ' ') || '—'}</td>
                      <td className="py-3.5 px-6 text-sm text-gray-700 capitalize">{tx.plan || '—'}</td>
                      <td className="py-3.5 px-6 text-sm text-gray-900 font-semibold text-right">${tx.amount?.toFixed(2)}</td>
                      <td className="py-3.5 px-6 text-center"><StatusBadge status={tx.payment_status} /></td>
                      <td className="py-3.5 px-6 text-center">
                        {tx.recurring ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                            <ArrowsClockwise size={12} /> Monthly
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">One-time</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Receipt size={40} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">No billing history yet</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
